import { useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Image,
  Progress,
  Text,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { useRouter } from "next/router";
import LogInModal from "../../src/authentication/LogInModal";
import LoadingSpinner from "../../src/components/common/LoadingSpinner";
import { useUser } from "../../src/hooks/useUser";
import Link from "next/link";
import { PartHeader } from "../../src/pms-search/student-pms/PartHeader";
import YourDetailsForm from "../../src/pms-search/student-pms/YourDetailsForm";
import { SymptomsForm } from "../../src/pms-search/student-pms/SymptomsForm";
import PreferencesForm from "../../src/pms-search/student-pms/PreferencesForm";
import PaymentForm from "../../src/pms-search/student-pms/PaymentForm";
import Head from "next/head";

const steps = [
  {
    title: "Your details",
    description: "Get started on your MatchPlus details!",
  },
  {
    title: "What's worrying you?",
    description: "What's worrying you and other symptoms you may face",
  },
  {
    title: "Preferences",
    description: "Your preferences to help find a great match",
  },
  {
    title: "Confirm your details",
    description: "Confirm your details and proceed to payment",
  },
];

const StudentMatchPlus = () => {
  const loggedIn = useSelector((state) => state.user.JWTToken);
  const [loaded, token] = useUser();

  const [step, setStep] = useState(0);
  const cancelRef = useRef();
  const router = useRouter();

  const closeAlert = (url = "/") => {
    router.push(url);
  };

  const goBack = () => {
    if (step > 0) {
      setStep(step - 1);
      return;
    }
    router.back();
  };

  return loaded ? (
    <div>
      <Head>
        <title>Welldoing.org - Student Matching Service</title>
        <meta name="title" content="Welldoing.org - Student Matching Service" />
        <meta
          name="description"
          content="Welldoing.org - Student Matching Service"
        />
        <meta
          name="og:description"
          content="Welldoing.org - Student Matching Service"
        />
        <link
          rel="canonical"
          href={`https://welldoing.org/student-matching-service`}
        />
      </Head>
      <nav className="flex py-2 px-4 justify-between fixed top-0 w-full flex-col sm:flex-row bg-white">
        <Link href="/" passHref prefetch={false} className="mb-2 sm:mb-0">
          <Image
            width={{ base: "150px", xl: "247px" }}
            height={{ base: "28px", xl: "46px" }}
            src="/images/homepage/wd-logo.png"
            alt="welldoing logo"
          />
        </Link>
        <div className="flex flex-1 flex-col items-start gap-2 max-w-[640px] mx-2">
          <Text as="h1" textStyle="h6" color="brand-300">
            Student MatchPlus
          </Text>
          <Progress
            className="w-full"
            value={(step / 3) * 100}
            size="xs"
            colorScheme="secondary"
          />
          <div className="flex items-center justify-between w-full text-sm text-brand-alt">
            <span>Your details</span>
            <span>Symptoms</span>
            <span>Preferences</span>
            <span>Payment</span>
          </div>
        </div>
        <div className="w-[150px] xl:w-[247px]" />
      </nav>

      {/* Log In modal */}
      {!loggedIn ? (
        <LogInModal
          title="Sign up to submit a MatchPlus request"
          emailValidation="false"
          signUpChoice="client"
          noRedirect
        />
      ) : (
        <div className="w-full flex flex-col items-center mt-12 sm:mt-2">
          <div className="flex flex-col items-center w-full max-w-[640px]">
            <PartHeader
              step={step}
              goBack={goBack}
              title={steps[step]?.title ?? "Enter details"}
              description={
                steps[step]?.description ?? "Fill out the form below"
              }
            />
            {step === 0 && <YourDetailsForm onNext={() => setStep(1)} />}
            {step === 1 && <SymptomsForm onNext={() => setStep(2)} />}
            {step === 2 && <PreferencesForm onNext={() => setStep(3)} />}
            {step === 3 && <PaymentForm />}
          </div>
        </div>
      )}
    </div>
  ) : (
    <LoadingSpinner />
  );
};

export default StudentMatchPlus;
